import { IHttpService, ILogService, IQService, IWindowService } from "angular";
import { StateService } from "angular-ui-router";
import { ApiClientService } from "./api-client.service";
import { SessionService } from "./session.service";

/**
 * The first implementation of the SessionService, which uses the API to get a token
 * and persist the token in the MVC session.
 */
export class SessionServiceV1 implements SessionService {
    isAuthenticated: boolean;
    constructor(private $http: IHttpService, private $log: ILogService,
        private $q: IQService, private $state: StateService, private $window: IWindowService,
        private apiClientService: ApiClientService, private apexApiUrl: string,
        apexIsAuthenticated: boolean, private apexProxyUserID: number) {
        'ngInject'
        this.$http = $http;
        this.$log = $log;
        this.$q = $q
        this.$state = $state;
        this.$window = $window;
        this.apexApiUrl = apexApiUrl
        this.apiClientService = apiClientService;
        this.apexProxyUserID = apexProxyUserID;
        this.isAuthenticated = apexIsAuthenticated;
    }

    isAuthorized() {
        return this.$q.resolve(this.isAuthenticated);
    }

    isImpersonated() {
        return this.$q.resolve(!!this.apexProxyUserID)
    }

    signIn(username: string, password: string, rememberMe: boolean) {
        const loginForm = this.$window.loginForm;

        if (!loginForm) {
            const error = "Attempted to perform sign in without a #loginForm element on the page"
            this.$log.error(error)
            return this.$q.reject(error)
        }

        loginForm.Input_Email.value = username
        loginForm.Input_Password.value = password
        loginForm.Input_RememberMe.value = !!rememberMe

        // Submit the login form in order to generate a refresh token in the session.
        loginForm.submit();
        // The service should return the refresh token directly, but this is not possible with the current implementation.
        // Return an unresolved promise to force the application to wait until the page has redirected due to form POST.
        return this.$q<void>((resolve, reject) => { });
    }

    signOut() {
        // Submit the form in this controller.
        localStorage.clear();
        sessionStorage.clear()
        const logoutForm = this.$window.logoutForm;
        this.isAuthenticated = false;
        if (!logoutForm) {
            this.$log.error("Attempted to log out using v1 session service, which requires #logoutForm, without one on the page");
            // The form post would send the user back to the login page, but without one, send the user there manually.
            this.$state.go("account.login");
        } else {
            // This will cause a refresh; the server will take care of actually ending the session.
            logoutForm.submit();
        }
        // Never resolve the asynchronous promise; the form will redirect the page.
        return this.$q<void>((resolve, reject) => { });
    }

    /**
     * Direct the user to sign in through an external service.
     * @param {string} loginProvider The name of the external login provider.
     * @param {string} providerKey The optional key provided allowing the application to authenticate through
     * the third-party service.
     */
    externalSignIn(loginProvider, providerKey) {
        // TODO: Dispatch logic for sending the user through the process of authenticating
        // through the login provider.
        return this.$q.reject(new Error("Not implemented"));
    }

    /**
     * Proxy log into the user with the given ID.
     * @param {number} userID The user whose account will be proxied into.
     */
    proxy(userID) {
        this.apexProxyUserID = null
        sessionStorage.clear();
        localStorage.clear();
        const form = this.$window.proxySignInForm as HTMLFormElement;
        if (!form) {
            this.$log.error("Attempted to proxy sign in without form on page");
            return this.$q.reject("Unable to find the proxy sign in form.")
        }
        form.action += `&userID=${userID}`;
        form.submit();
        return this.$q<void>(() => {});
    }

    endProxy() {
        this.apexProxyUserID = null
        sessionStorage.clear();
        localStorage.clear();
        const form = this.$window.proxySignOutForm as HTMLFormElement;
        if (!form) {
            this.$log.error("Attempted to proxy sign out without form on page");
            return this.$q.reject("Unable to find the proxy sign out form.")
        }
        form.submit();
        return this.$q<void>(() => {});
    }

    renewClaims() {
        const form = this.$window.refreshSignInForm as HTMLFormElement;
        if (form) {
            form.submit();
        }
        // Never resolve the asynchronous promise; the form will redirect the page.
        return this.$q<void>((resolve, reject) => { });
    }
}
